import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, TrendingUp, Users, Flame, Award , ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import ParticipantProfileModal from '@/components/ParticipantProfileModal';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  bronze: 'bg-amber-600',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  diamond: 'bg-cyan-500'
};

const Leaderboard = ({ user }) => {
  const { colors } = useTheme();
  const [individualLeaders, setIndividualLeaders] = useState([]);
  const [groupLeaders, setGroupLeaders] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  useEffect(() => {
    fetchLeaderboards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const token = localStorage.getItem('token');
      const [individualRes, groupRes, teamRes, rankRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboard/individual`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/leaderboard/group`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/leaderboard/team`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/leaderboard/my-rank`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (individualRes.ok) setIndividualLeaders(await individualRes.json());
      if (groupRes.ok) setGroupLeaders(await groupRes.json());
      if (teamRes.ok) setTeamLeaders(await teamRes.json());
      if (rankRes.ok) setMyRank(await rankRes.json());
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    }
    setLoading(false);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" fill="currentColor" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-lg font-bold text-muted-foreground w-6 text-center">#{rank}</span>;
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-400';
    if (rank === 1) return 'bg-gradient-to-r from-yellow-100 to-amber-100';
    if (rank === 2) return 'bg-gradient-to-r from-gray-100 to-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-amber-100 to-orange-100';
    return 'bg-white hover:bg-gray-50';
  };

  const renderLeaderboardItem = (leader, index, showGroup = false) => {
    const isCurrentUser = leader.id === user?.id;
    const rank = leader.rank || index + 1;
    
    return (
      <motion.div
        key={leader.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        onClick={() => setSelectedParticipant(leader.id)}
        className={`p-4 rounded-2xl cursor-pointer transition-all shadow-sm hover:shadow-md ${getRankBg(rank, isCurrentUser)}`}
        data-testid={`leaderboard-item-${leader.id}`}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-10 flex justify-center">
            {getRankIcon(rank)}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-bold truncate ${isCurrentUser ? 'text-purple-700' : ''}`}>
                {leader.name}
                {isCurrentUser && <span className="text-purple-500 ml-1">(You)</span>}
              </span>
              {leader.current_streak >= 3 && (
                <span className="flex items-center text-orange-500 text-sm" title={`${leader.current_streak} day streak`}>
                  <Flame className="w-4 h-4" />
                  {leader.current_streak}
                </span>
              )}
            </div>
            {/* Badges preview */}
            {leader.badges?.length > 0 && (
              <div className="flex gap-1 mt-1">
                {leader.badges.slice(0, 4).map((badgeId, idx) => (
                  <span key={idx} className="text-xs opacity-70">
                    {badgeId === 'legend' ? '💎' : 
                     badgeId === 'goal_master' ? '🏆' : 
                     badgeId === 'on_fire' ? '🔥' :
                     badgeId === 'rising_star' ? '⭐' : '🏅'}
                  </span>
                ))}
                {leader.badges.length > 4 && (
                  <span className="text-xs text-muted-foreground">+{leader.badges.length - 4}</span>
                )}
              </div>
            )}
          </div>

          {/* Points */}
          <div className="text-right">
            <p className={`font-heading text-xl font-bold ${
              rank === 1 ? 'text-yellow-600' : 
              rank === 2 ? 'text-gray-500' : 
              rank === 3 ? 'text-amber-600' : 
              isCurrentUser ? 'text-purple-600' : 'text-foreground'
            }`}>
              {(leader.total_points || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">points</p>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="p-6 md:p-8 rounded-b-3xl shadow-lg mb-6" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-white" /></button>
            <Trophy className="w-10 h-10" style={{ color: colors?.mode === 'dark' ? colors?.primary : '#fff' }} />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)', color: colors?.text_on_primary || '#FFFFFF' }}>Leaderboards</h1>
          </div>
          <p className="text-lg opacity-90" style={{ color: colors?.text_on_primary || '#FFFFFF' }}>Compete, climb, and conquer! 🔥</p>
          
          {/* My Rank Card */}
          {myRank && (
            <Card className="bg-white/15 backdrop-blur border-white/20 p-4 rounded-2xl mt-4" style={{color: "white"}}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-75">Your SYEP Rank</p>
                  <p className="text-3xl font-bold">#{myRank.overall_rank} <span className="text-lg opacity-75">of {myRank.overall_total}</span></p>
                </div>
                {myRank.group_rank && (
                  <div className="text-right">
                    <p className="text-sm opacity-75">Group Rank</p>
                    <p className="text-3xl font-bold">#{myRank.group_rank} <span className="text-lg opacity-75">of {myRank.group_total}</span></p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <Tabs defaultValue="all-syep" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6 h-14 bg-white rounded-2xl p-1 shadow-sm">
            <TabsTrigger
              data-testid="all-syep-tab"
              value="all-syep"
              className="rounded-xl font-semibold data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Trophy className="w-4 h-4 mr-2" />
              All SYEP
            </TabsTrigger>
            <TabsTrigger
              data-testid="my-group-tab"
              value="my-group"
              className="rounded-xl font-semibold data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              My Group
            </TabsTrigger>
            <TabsTrigger
              data-testid="teams-tab"
              value="teams"
              className="rounded-xl font-semibold data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Teams
            </TabsTrigger>
          </TabsList>

          {/* All SYEP Leaderboard */}
          <TabsContent value="all-syep">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">Top 30 Participants</h2>
              <Badge variant="outline" className="text-sm">
                Live Rankings
              </Badge>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : (
              <div className="space-y-3">
                {individualLeaders.map((leader, index) => renderLeaderboardItem(leader, index))}
                {individualLeaders.length === 0 && (
                  <Card className="p-8 text-center rounded-3xl">
                    <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No participants yet</p>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* My Group Leaderboard */}
          <TabsContent value="my-group">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">My Group Rankings</h2>
              <Badge variant="outline" className="text-sm">
                {groupLeaders.length} members
              </Badge>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : groupLeaders.length > 0 ? (
              <div className="space-y-3">
                {groupLeaders.map((leader, index) => renderLeaderboardItem(leader, index, true))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-3xl">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">You&apos;re not assigned to a group yet</p>
                <p className="text-sm text-muted-foreground mt-2">Contact your lead monitor for group assignment</p>
              </Card>
            )}
          </TabsContent>

          {/* Teams Leaderboard */}
          <TabsContent value="teams">
            <div className="mb-4">
              <h2 className="font-heading text-xl font-bold">Team Rankings</h2>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : teamLeaders.length > 0 ? (
              <div className="space-y-3">
                {teamLeaders.map((team, index) => (
                  <motion.div
                    key={team.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 rounded-2xl ${getRankBg(index + 1, false)} shadow-sm`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 flex justify-center">
                        {getRankIcon(index + 1)}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold">{team.name}</p>
                        <p className="text-sm text-muted-foreground">{team.member_count || 0} members</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-xl font-bold">{(team.total_points || 0).toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">team points</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center rounded-3xl">
                <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No teams created yet</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Participant Profile Modal */}
      <ParticipantProfileModal
        participantId={selectedParticipant}
        isOpen={!!selectedParticipant}
        onClose={() => setSelectedParticipant(null)}
      />

      <Navigation role="youth" />
    </div>
  );
};

export default Leaderboard;
